import { createFeatureFlags } from 'src/v2/shared/feature-flags';
import { GravionStorageKeys } from 'src/v2/shared/storage/keys';
import { Stage, CURRENT_STAGE } from 'src/contstant/apiEndpoints';

// ---------------------------------------------------------------------------
// Flag keys — const object for type safety
// ---------------------------------------------------------------------------

export const FEATURE_FLAGS = {
  enableDivisionalConfiguration: 'enableDivisionalConfiguration',
  enableTaskReopen: 'enableTaskReopen',
  enableCIProjectPrompt: 'enableCIProjectPrompt',
  enableMultiAssignee: 'enableMultiAssignee',
  enableEnhancedResponseTypes: 'enableEnhancedResponseTypes',
  enableV2Desktop: 'enableV2Desktop',
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// ---------------------------------------------------------------------------
// Human-readable descriptions (shown in the dev toggle panel)
// ---------------------------------------------------------------------------

export const featureFlagDescriptions: Record<FeatureFlagKey, string> = {
  [FEATURE_FLAGS.enableDivisionalConfiguration]:
    'Divisional Configuration page (NA Reason Codes)',
  [FEATURE_FLAGS.enableTaskReopen]: 'Reopen completed tasks',
  [FEATURE_FLAGS.enableCIProjectPrompt]: 'CI project prompt on perfect audit score',
  [FEATURE_FLAGS.enableMultiAssignee]: 'Multiple assignees for an audit',
  [FEATURE_FLAGS.enableEnhancedResponseTypes]:
    'Enhanced Response Types (ERT) for questions',
  [FEATURE_FLAGS.enableV2Desktop]: 'Enable Stencil UI',
};

// ---------------------------------------------------------------------------
// Defaults — all false per shared-lib convention.
// Stage-based values are passed as apiFlags at the Provider level.
// ---------------------------------------------------------------------------

const defaultFeatureFlags: Record<FeatureFlagKey, boolean> = {
  [FEATURE_FLAGS.enableDivisionalConfiguration]: false,
  [FEATURE_FLAGS.enableTaskReopen]: false,
  [FEATURE_FLAGS.enableCIProjectPrompt]: false,
  [FEATURE_FLAGS.enableMultiAssignee]: false,
  [FEATURE_FLAGS.enableEnhancedResponseTypes]: false,
  [FEATURE_FLAGS.enableV2Desktop]: false,
};

// ---------------------------------------------------------------------------
// Stage-based flag values — passed as apiFlags to the Provider.
// These represent what the environment enables by default (medium priority).
// In non-prod, local storage overrides can still flip them.
// ---------------------------------------------------------------------------

const FEATURE_FLAGS_BY_STAGE: Record<Stage, Record<FeatureFlagKey, boolean>> = {
  [Stage.TEST]: {
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
  },
  [Stage.BETA]: {
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
  },
  [Stage.GAMMA]: {
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
  },
  [Stage.PROD]: {
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
    enableEnhancedResponseTypes: false,
    enableV2Desktop: false,
  },
};

/**
 * Returns the stage-based flag values for the current environment.
 * Used as apiFlags in the Provider.
 */
export const getFeatureFlagsForStage = (): Record<FeatureFlagKey, boolean> => {
  return FEATURE_FLAGS_BY_STAGE[CURRENT_STAGE] ?? FEATURE_FLAGS_BY_STAGE[Stage.BETA];
};

// ---------------------------------------------------------------------------
// Create the feature flag system
// ---------------------------------------------------------------------------

const {
  Provider: FeatureFlagProvider,
  useFeatureFlagContext,
  useFeatureFlag,
  useFeatureFlags,
} = createFeatureFlags<FeatureFlagKey>({
  flags: FEATURE_FLAGS,
  descriptions: featureFlagDescriptions,
  defaults: defaultFeatureFlags,
  storageKey: GravionStorageKeys.featureFlags,
  isProduction: () => CURRENT_STAGE === Stage.PROD,
  isDevMode: () => CURRENT_STAGE !== Stage.PROD,
});

export { FeatureFlagProvider, useFeatureFlagContext, useFeatureFlag, useFeatureFlags };

// Legacy alias — the old provider was named FeatureFlagsProvider (plural).
// Keep the export so index.tsx doesn't need an import change.
export { FeatureFlagProvider as FeatureFlagsProvider };

// Legacy type alias — v2/shared/auth/useFeatureFlags.ts re-exports this type.
// In the new system the equivalent is Record<FeatureFlagKey, boolean>.
export type FeatureFlags = Record<FeatureFlagKey, boolean>;
